<template>
  <a-select
    :id="filterElement.idForProp('filter')"
    :placeholder="placeholder"
    :options="options"
    :selected-option-ids="selectedOptionIds"
    v-model="queryIds"
    @search:finished="handleSearchFinished"
    @selected="handleSelectAll"
    @deselected="handleDeselectAll"
    bind-by-array
    multiselect
    show-checkboxes
    stay-open
  ></a-select>
</template>

<script lang="ts">
  import Vue from "vue";
  import {IVOptionSetLocal} from "../../../common/interfaces/vSelectInterfaces";
  import ObjectModelProperty from "../../../common/models/objectModelProperties/objectModelProperty";
  import {SelectOptionString} from "../../../common/enums/selectOption";
  import listViewFilterMixin from '../../../common/mixins/listViews/listViewFilterMixin'
  import {Hash} from "../../../common/interfaces/hashInterfaces";
  import { uniqBy } from 'lodash'
  import {ArrayHelper} from "~/helpers/arrayHelper";
  import filterElementPropertyFilterableMixin from "../../listViewBuilder/mixins/filter-element-property-filterable-mixin";

  const VALUE_KEY = 'id';
  const LABEL_KEY = 'label_title';

  export default Vue.extend({
    mixins: [
      listViewFilterMixin,
      filterElementPropertyFilterableMixin,
    ],
    data: function () {
      return {
        queryIds: [],
        filteredPropertyOptions: [],
      };
    },
    created() {
      this.initializeQueryIds();
    },
    computed: {
      allOptionsOption(): Hash<string | boolean> {
        return {[LABEL_KEY]: "Select all", [VALUE_KEY]: SelectOptionString.All, isPinned: true};
      },
      dropdownOptions(): Array<ObjectModelProperty | Hash<string>> {
        return [
          this.allOptionsOption,
          ...this.objectModelProperties,
        ]
      },
      objectModelProperties(): ObjectModelProperty[] {
        if (this.filterElement.propertyToFilter.linked_relation_mapped_properties.length > 0) {
          const mapped = this.filterElement.propertyToFilter.linked_relation_mapped_properties.map(prop => prop.mapped_object_model_properties)
          return uniqBy<ObjectModelProperty>(mapped.flat(), "label_title")
        }
        return this.filterElement.propertyToFilter.objectModelPropertiesArray
      },
      options(): IVOptionSetLocal {
        return {
          labelKey: LABEL_KEY,
          valueKey: VALUE_KEY,
          options: this.dropdownOptions,
        };
      },
      selectedOptionIds(): Array<string> {
        const selectedIds = this.queryIds.slice();

        // show "Select all" as checked if all options are selected
        if (this.allPropertyOptionsSelected) {
          selectedIds.push(this.allOptionsOption.id);
        }

        return selectedIds;
      },
      allPropertyOptionsSelected(): boolean {
        let props: Array<ObjectModelProperty>;
        if (this.filteredPropertyOptions.length > 0) {
          props = this.filteredPropertyOptions;
        } else {
          props = this.objectModelProperties;
        }

        return props.length > 0 && props.every((prop) => this.queryIds.includes(prop.id));
      },
      placeholder(): string {
        switch (this.queryIds.length) {
          case 0:
            return this.filterElement.placeholder_text || "Select one...";
          case 1:
            return this.objectModelProperties.find(p => p.id === this.queryIds[0])[LABEL_KEY]
          default:
            const selected = this.queryIds.length;
            const total = this.objectModelProperties.length;
            return `${selected}/${total} Selected`;
        }
      },
    },
    methods: {
      clearQueryIfInvalid(): void {
        const labels = decodeURIComponent(this.queryValue).split(',').filter(t => !!t);
        if (!labels.every(label => !!this.objectModelProperties.find(prop => prop.label_title === label))) {
          this.clearFilter({mode: 'replace'});
        }
      },
      initializeQueryIds(): void {
        if (this.queryIds.length > 0 || !this.queryValue) return;

        const labels = decodeURIComponent(this.queryValue).split(',').filter(t => !!t);
        this.queryIds = this.getSelectedValueIdentifiers({selectedValues: labels, fromKey: LABEL_KEY, toKey: VALUE_KEY});
      },
      getSelectedValueIdentifiers(instructions: {selectedValues: string[], fromKey: string, toKey: string}): string[] {
        // transforms selectedValues from one identifier type to another, i.e. from :label_title to :id or vice versa
        const {selectedValues, fromKey, toKey} = instructions;

        return this.objectModelProperties.reduce((identifiers, property) => {
          if (selectedValues.includes(property[fromKey])) {
            identifiers.push(property[toKey]);
          }
          return identifiers;
        }, []);
      },
      setDefaultFilter(): void {
        if (!!this.queryValue || this.isInDashBoardDrillDown) {
          return;
        }
        if (this.filterElement.default_property_filter_option_ids.length > 0) {
          this.queryIds = this.filterElement.default_property_filter_option_ids.slice();
        } else {
          this.clearFilter();
        }
      },
      setFilter(): void {
        this.addFilterToQuery(this.selectedIdsQueryValues().join(','));
      },
      selectedIdsQueryValues(): string[] {
        return this.getSelectedValueIdentifiers({selectedValues: this.queryIds, fromKey: VALUE_KEY, toKey: LABEL_KEY});
      },
      handleSearchFinished(results: Array<{ result: Array<ObjectModelProperty | Hash<string>> }>): void {
        const options = results && results[0]?.result;
        this.filteredPropertyOptions = options?.filter(o => o.id !== this.allOptionsOption.id) || [];
      },
      handleSelectAll(lastSelectedOption: { [VALUE_KEY] }): void {
        if (lastSelectedOption[VALUE_KEY] !== this.allOptionsOption[VALUE_KEY]) return;

        this.queryIds = this.objectModelProperties.map(p => p[VALUE_KEY]);
      },
      handleDeselectAll(lastSelectedOption: { [VALUE_KEY] }): void {
        if (lastSelectedOption[VALUE_KEY] === this.allOptionsOption[VALUE_KEY]) {
          this.queryIds = [];
        } else {
          ArrayHelper.removeFirst(this.queryIds, this.allOptionsOption[VALUE_KEY]);
        }
      },
      reset(): void {
        this.queryIds = [];
      },
    },
    watch: {
      queryIds(newVal, oldVal) {
        if (newVal.length === 0 && oldVal.length === 0) return; // prevent changing filter on component initialization

        if (newVal.length === 0) {
          this.clearFilter();
        } else {
          this.setFilter();
        }
      },
    }
  });
</script>